import * as React from "react";
import { Box, Button, IconButton, Popover } from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";

import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";

import { addDays, subDays, differenceInCalendarDays, endOfDay, startOfDay, min } from "date-fns";

const DEFAULT_MAX_RANGE_DAYS = 7;

export default function RangeSelector({ range, setRange, maxRangeDays = DEFAULT_MAX_RANGE_DAYS, singleDate = false }) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const hasLimit = maxRangeDays != null;

  const rangeLength = singleDate ? 1 : differenceInCalendarDays(range.endDate, range.startDate) + 1;

  const label = singleDate
    ? range.startDate.toLocaleDateString()
    : `${range.startDate.toLocaleDateString()} - ${range.endDate.toLocaleDateString()}`;

  const prevRange = {
    startDate: startOfDay(subDays(range.startDate, rangeLength)),
    endDate: endOfDay(subDays(range.startDate, 1)),
    key: "selection",
  };

  // Clamp the next range so it never goes into the future (no data exists there)
  const today = endOfDay(new Date());
  const todayStart = startOfDay(new Date());

  const idealNextEnd = endOfDay(addDays(range.endDate, rangeLength));
  const clampedNextEnd = idealNextEnd > today ? today : idealNextEnd;

  const clampedNextStart = startOfDay(subDays(clampedNextEnd, rangeLength - 1));

  const nextRange = {
    startDate: clampedNextStart,
    endDate: clampedNextEnd,
    key: "selection",
  };

  const nextDisabled = range.endDate >= today || startOfDay(range.endDate) >= todayStart;

  return (
    <Box display="flex" justifyContent="center" alignItems="center">
      <IconButton onClick={() => setRange(prevRange)}>
        <ArrowLeftIcon />
      </IconButton>

      <Button
        style={{ fontWeight: "bold", fontSize: 20 }}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        startIcon={<CalendarMonthIcon />}
      >
        {label}
      </Button>

      <IconButton onClick={() => setRange(nextRange)} disabled={nextDisabled}>
        <ArrowRightIcon />
      </IconButton>

      {singleDate ? (
        <DatePopup anchorEl={anchorEl} setAnchorEl={setAnchorEl} range={range} setRange={setRange} />
      ) : (
        <RangePopup
          anchorEl={anchorEl}
          setAnchorEl={setAnchorEl}
          range={range}
          setRange={setRange}
          maxRangeDays={hasLimit ? maxRangeDays : null}
        />
      )}
    </Box>
  );
}

function DatePopup({ anchorEl, setAnchorEl, range, setRange }) {
  const today = endOfDay(new Date());

  return (
    <Popover
      open={!!anchorEl}
      anchorEl={anchorEl}
      onClose={() => setAnchorEl(null)}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
    >
      <Box p={2}>
        <DatePicker
          label="Select date"
          format="DD/MM/YYYY"
          value={dayjs(range.startDate)}
          maxDate={dayjs(today)}
          onChange={(value) => {
            if (!value) return;

            const date = value.toDate();

            setAnchorEl(null);
            setRange({
              startDate: startOfDay(date),
              endDate: endOfDay(date),
              key: "selection",
            });
          }}
        />
      </Box>
    </Popover>
  );
}

function RangePopup({ anchorEl, setAnchorEl, range, setRange, maxRangeDays }) {
  const [localRange, setLocalRange] = React.useState(range);
  const hasLimit = maxRangeDays != null;

  React.useEffect(() => {
    setLocalRange(range);
  }, [range]);

  const today = endOfDay(new Date());

  const startDate = localRange.startDate;

  const minEndDate = startDate;
  const maxEndDate = hasLimit ? min([addDays(startDate, maxRangeDays - 1), today]) : today;

  const handleApply = () => {
    setAnchorEl(null);
    setRange(localRange);
  };

  return (
    <Popover
      open={!!anchorEl}
      anchorEl={anchorEl}
      onClose={() => setAnchorEl(null)}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
    >
      <Box p={2} display="flex" flexDirection="column" gap={2} width={300}>
        <DatePicker
          label="Start date"
          format="DD/MM/YYYY"
          value={dayjs(localRange.startDate)}
          maxDate={dayjs(today)}
          onChange={(value) => {
            if (!value) return;

            const newStart = startOfDay(value.toDate());
            const wasSingleDay = differenceInCalendarDays(localRange.endDate, localRange.startDate) === 0;
            let newEnd = localRange.endDate;

            // Keep a single-day selection as single-day when moving the start date.
            if (wasSingleDay) {
              newEnd = endOfDay(newStart);
            }

            if (newEnd < newStart) {
              newEnd = endOfDay(newStart);
            }

            if (hasLimit) {
              const maxAllowed = addDays(newStart, maxRangeDays - 1);
              if (newEnd > maxAllowed) {
                newEnd = endOfDay(maxAllowed);
              }
            }

            setLocalRange({
              startDate: newStart,
              endDate: endOfDay(newEnd),
              key: "selection",
            });
          }}
        />

        <DatePicker
          label="End date"
          format="DD/MM/YYYY"
          value={dayjs(localRange.endDate)}
          minDate={dayjs(minEndDate)}
          maxDate={dayjs(maxEndDate)}
          onChange={(value) => {
            if (!value) return;

            setLocalRange((prev) => ({
              ...prev,
              endDate: endOfDay(value.toDate()),
            }));
          }}
        />

        <Box display="flex" justifyContent="flex-end">
          <Button variant="contained" size="small" onClick={handleApply}>
            Apply
          </Button>
        </Box>
      </Box>
    </Popover>
  );
}
